/**
 * What the `/downloads` card says about a Studio export — STUDIO_FIX_PROMPT.md S-43.
 *
 * The card's generic line reads the FIRST input's extension and the option tree's `toExtension`,
 * which for a Studio card is "Converted MP4 to MP4" (`d1920-downloads-card.jpg`): the card is named
 * `<name>.mp4` by `submit.ts` and the pin is `MP4`, so both halves of the sentence are the output.
 * And `replaceExtension(card.name, options.toExtension)` upper-cases the suffix into `my-video.MP4`.
 *
 * Both are answered here from the composition the card carries -- the same field `isStudioWorkflow`
 * reads (`reedit.ts`), so a card that re-opens in the Studio and a card that describes itself as a
 * Studio video are one fact. `file-list-item.component.tsx` asks these two questions first and
 * falls through to its own sentence when the answer is null, on the pattern its picker branch set
 * (`pick-another-frame`): the feature module says what is feature-specific, the card renders it.
 *
 * PURE: no services, no React. The composition is read structurally (`tracks[].clips[].kind`) so a
 * stored card from any build of the wire shape answers, and a malformed one answers null rather than
 * a wrong count.
 */
import { COPY } from '../views/studio.copy';
import { STUDIO_CONTAINER_EXT } from './build-request';
import { studioCompositionOf } from './reedit';
import type { StudioWorkflowFacts } from './reedit';

export interface StudioCardMix {
    photos: number;
    videos: number;
    songs: number;
}

/** The input mix of a Studio card's composition, or null when the card is not a Studio export. */
export function studioCardMixOf(fileUpload: StudioWorkflowFacts | null | undefined): StudioCardMix | null {
    const composition = studioCompositionOf(fileUpload);
    if (!composition) return null;
    const tracks = Array.isArray(composition.tracks) ? composition.tracks : [];
    const mix: StudioCardMix = { photos: 0, videos: 0, songs: 0 };
    for (const track of tracks as Array<Record<string, unknown>>) {
        const clips = Array.isArray(track?.clips) ? track.clips : [];
        for (const clip of clips as Array<Record<string, unknown>>) {
            switch (clip?.kind) {
                case 'PHOTO': mix.photos += 1; break;
                case 'VIDEO': mix.videos += 1; break;
                case 'AUDIO': mix.songs += 1; break;
                default: break;
            }
        }
    }
    return mix;
}

/**
 * "Studio video · 5 photos · 1 video · 1 song" -- photos first, then only the parts the project has.
 * A composition with no readable clips still says "Studio video": that much is true of it.
 */
export function studioCardLine(fileUpload: StudioWorkflowFacts | null | undefined): string | null {
    const mix = studioCardMixOf(fileUpload);
    if (!mix) return null;
    const parts = [
        COPY.cardStudioVideo,
        mix.photos > 0 ? COPY.cardPhotos(mix.photos) : '',
        mix.videos > 0 ? COPY.cardVideos(mix.videos) : '',
        mix.songs > 0 ? COPY.cardSongs(mix.songs) : '',
    ].filter(Boolean);
    return parts.join(' · ');
}

/**
 * The card's file name with a LOWER-CASE `.mp4`, or null for a non-Studio card.
 *
 * `submit.ts` names the card `<typed name>.mp4`; the generic path re-suffixes it with the option
 * pin `MP4`. The Studio pins one container, so the suffix is that container's extension as a file
 * would carry it -- whatever case the stored name ended up in.
 */
export function studioCardFileName(fileUpload: (StudioWorkflowFacts & { name?: string }) | null | undefined): string | null {
    if (!studioCompositionOf(fileUpload)) return null;
    const name = String(fileUpload?.name ?? '').trim();
    if (!name) return null;
    const dot = name.lastIndexOf('.');
    const base = dot > 0 ? name.slice(0, dot) : name;
    return `${base}.${STUDIO_CONTAINER_EXT}`;
}
